import get from 'lodash/get';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/map';
import {Observable} from 'rxjs/Observable';
import {buildAssetProjectLink$, buildOrganizationLink$, buildProjectLink$} from '../../app/navigation/utils';
import {getId} from '../../core-utilities/domain/ReduxUtils';
import {BreadcrumbType} from '../../core-utilities/enums/BreadcrumbType';
import {getStateParamsInView$} from '../../core-utilities/redux';
import {TaskType} from '../../tasks/enums/TaskType';
import {assetTasksStates, projectTasksStates} from '../../tasks/router-config';
import {
  ASSET_FILE_REQUIREMENT_COLLECTION,
  ASSET_FORM_REQUIREMENT_COLLECTION,
  FILE_REQUIREMENT_COLLECTION,
  FORM_REQUIREMENT_COLLECTION,
} from '../domain/config';
import {
  getAssetFileRequirement,
  getAssetFormRequirement,
  getProjectFileRequirement,
  getProjectFormRequirement,
} from '../reducers/state';
import {
  assetFileRequirementStates,
  assetFormRequirementStates,
  projectFileRequirementStates,
  projectFormRequirementStates,
} from '../router-config';
import {assetFileRequirementRouterState} from './files/assetFileRequirementRouterState';
import {projectFileRequirementRouterState} from './files/projectFileRequirementRouterState';
import {assetFormRequirementRouterState} from './forms/assetFormRequirementRouterState';
import {projectFormRequirementRouterState} from './forms/projectFormRequirementRouterState';


/**
 * @param {String} collection
 * @param {Observable<any>} state$
 * @param {Observable<any>} props$
 * @return {{stateParams$: Observable<any>, requirement$: Observable<any>, breadcrumbPath$: Observable<Array<NavigationBreadcrumbPath>>}}
 */
export function buildPropsByCollection(collection, state$, props$) {
  switch (collection) {
    case ASSET_FILE_REQUIREMENT_COLLECTION:
      return buildAssetFileRequirementProps(state$, props$);

    case ASSET_FORM_REQUIREMENT_COLLECTION:
      return buildAssetFormRequirementProps(state$, props$);

    case FILE_REQUIREMENT_COLLECTION:
      return buildProjectFileRequirementProps(state$, props$);

    case FORM_REQUIREMENT_COLLECTION:
      return buildProjectFormRequirementProps(state$, props$);
  }
}

export function buildAssetFileRequirementProps(state$, props$) {
  return buildTaskProps({
    state$,
    props$,
    taskType: TaskType.ASSET,
    taskRouterStates: assetTasksStates,
    routerStates: assetFileRequirementStates,
    stateNames: assetFileRequirementRouterState.options.stateNames,
    getItemState: getAssetFileRequirement,
  });
}

export function buildAssetFormRequirementProps(state$, props$) {
  return buildTaskProps({
    state$,
    props$,
    taskType: TaskType.ASSET,
    taskRouterStates: assetTasksStates,
    routerStates: assetFormRequirementStates,
    stateNames: assetFormRequirementRouterState.options.stateNames,
    getItemState: getAssetFormRequirement,
  });
}

export function buildProjectFileRequirementProps(state$, props$) {
  return buildTaskProps({
    state$,
    props$,
    taskType: TaskType.PROJECT,
    taskRouterStates: projectTasksStates,
    routerStates: projectFileRequirementStates,
    stateNames: projectFileRequirementRouterState.options.stateNames,
    getItemState: getProjectFileRequirement,
  });
}

export function buildProjectFormRequirementProps(state$, props$) {
  return buildTaskProps({
    state$,
    props$,
    taskType: TaskType.PROJECT,
    taskRouterStates: projectTasksStates,
    routerStates: projectFormRequirementStates,
    stateNames: projectFormRequirementRouterState.options.stateNames,
    getItemState: getProjectFormRequirement,
  });
}

/**
 * @param {Observable<any>} state$
 * @param {Observable<any>} props$
 * @param {TaskType} taskType
 * @param {RouterTaskStates} taskRouterStates
 * @param {RouterRequirementStates} routerStates
 * @param {Array<String>} stateNames
 * @param getItemState
 * @return {{stateParams$: Observable<any>, requirement$: Observable<any>, breadcrumbPath$: Observable<Array<NavigationBreadcrumbPath>>}}
 */
function buildTaskProps({state$, props$, taskType, taskRouterStates, routerStates, stateNames, getItemState}) {
  const stateParams$ = getStateParamsInView$(state$, stateNames)
    .distinctUntilChanged();

  const requirement$ = state$
    .map(getItemState)
    .pluck('item');

  const breadcrumbPath$ = buildBreadcrumbPath$({
    state$,
    taskType,
    taskRouterStates,
    routerStates,
    requirement$,
  });

  return {
    stateParams$,
    requirement$,
    breadcrumbPath$,
  };
}

/**
 * @param {Observable<any>} state$
 * @param {TaskType} taskType
 * @param {RouterTaskStates} taskRouterStates
 * @param {RouterRequirementStates} routerStates
 * @param {Observable<any>} requirement$
 * @return {Observable<Array<NavigationBreadcrumbPath>>}
 */
function buildBreadcrumbPath$({state$, taskType, taskRouterStates, routerStates, requirement$}) {
  const reqLink$ = requirement$
    .map(requirement => !requirement ? {} : [{
      id: getId(requirement.task),
      label: get(requirement, 'task.name'),
      type: BreadcrumbType.task,
      to: taskRouterStates.requirements,
      toParams: {taskId: getId(requirement.task)},
    }, {
      id: getId(requirement),
      label: requirement.name,
      type: BreadcrumbType.requirement,
      to: routerStates.details,
      toParams: {requirementId: getId(requirement)},
    }]);

  if (taskType === TaskType.ASSET) {
    return Observable
      .combineLatest(
        buildOrganizationLink$(state$),
        buildProjectLink$(state$),
        buildAssetProjectLink$(state$),
        reqLink$,
        (orgLink, projectLink, assetProjectLink, reqLinks) => [orgLink, projectLink, assetProjectLink, ...reqLinks]
      );
  } else {
    return Observable
      .combineLatest(
        buildOrganizationLink$(state$),
        buildProjectLink$(state$),
        reqLink$,
        (orgLink, projectLink, reqLinks) => [orgLink, projectLink, ...reqLinks]
      );
  }
}



// WEBPACK FOOTER //
// ./src/js/requirements/redux/utils.js